import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Box, Button, Chip, Divider } from '@mui/material';
import { ArrowBack, AccessTime, Person } from '@mui/icons-material';
import PublicHeader from '../PublicHeader/PublicHeader';
import {
  ArticleHeader,
  ArticleMeta,
  ArticleContent
} from './BlogPost.styles';

function BlogPost() {
  const { t } = useTranslation();
  const { postId } = useParams();
  const navigate = useNavigate();

  // Blog post content mapping
  const blogContent = {
    'standing-desk-benefits': {
      title: t('blogPost1Title'),
      category: t('categoryHealth'),
      readTime: '5 min',
      author: 'StandStrong Team',
      date: '2025-10-01',
      content: t('blogPost1Content')
    },
    'optimal-desk-height': {
      title: t('blogPost2Title'),
      category: t('categoryErgonomics'),
      readTime: '7 min',
      author: 'StandStrong Team',
      date: '2025-09-28',
      content: t('blogPost2Content')
    },
    'standing-sitting-rhythm': {
      title: t('blogPost3Title'),
      category: t('categoryProductivity'),
      readTime: '6 min',
      author: 'StandStrong Team',
      date: '2025-09-25',
      content: t('blogPost3Content')
    },
    'team-motivation': {
      title: t('blogPost4Title'),
      category: t('categoryTeams'),
      readTime: '5 min',
      author: 'StandStrong Team',
      date: '2025-09-20',
      content: t('blogPost4Content')
    },
    'homeoffice-ergonomics': {
      title: t('blogPost5Title'),
      category: t('categoryErgonomics'),
      readTime: '8 min',
      author: 'StandStrong Team',
      date: '2025-09-15',
      content: t('blogPost5Content')
    },
    'back-pain-prevention': {
      title: t('blogPost6Title'),
      category: t('categoryHealth'),
      readTime: '6 min',
      author: 'StandStrong Team',
      date: '2025-09-10',
      content: t('blogPost6Content')
    }
  };

  const post = blogContent[postId];

  if (!post) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4">{t('postNotFound')}</Typography>
        <Button onClick={() => navigate('/blog')} sx={{ mt: 2 }}>
          {t('backToBlog')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflowX: 'hidden' }}>
      <PublicHeader />

      {/* Back Button */}
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 3 }}
        >
          {t('backToBlog')}
        </Button>
      </Container>

      {/* Article */}
      <Container maxWidth="md" sx={{ flexGrow: 1, pb: 8 }}>
        <ArticleHeader>
          <Chip label={post.category} color="primary" sx={{ mb: 2 }} />
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
              wordBreak: 'break-word'
            }}
          >
            {post.title}
          </Typography>
          <ArticleMeta>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person fontSize="small" />
              <Typography variant="body2">{post.author}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime fontSize="small" />
              <Typography variant="body2">{post.readTime}</Typography>
            </Box>
            <Typography variant="body2">{post.date}</Typography>
          </ArticleMeta>
        </ArticleHeader>

        <Divider sx={{ my: 4 }} />

        <ArticleContent>
          {/* Render content as HTML or split by paragraphs */}
          {post.content.split('\n\n').map((paragraph, index) => {
            // Check if paragraph is a heading (starts with ##)
            if (paragraph.startsWith('## ')) {
              return (
                <Typography
                  key={index}
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{ mt: 4, mb: 2, fontWeight: 600 }}
                >
                  {paragraph.replace('## ', '')}
                </Typography>
              );
            }
            // Check if paragraph is a subheading (starts with ###)
            if (paragraph.startsWith('### ')) {
              return (
                <Typography
                  key={index}
                  variant="h6"
                  component="h3"
                  gutterBottom
                  sx={{ mt: 3, mb: 1.5, fontWeight: 600 }}
                >
                  {paragraph.replace('### ', '')}
                </Typography>
              );
            }
            // Check if paragraph is a list item (starts with -)
            if (paragraph.startsWith('- ')) {
              const items = paragraph.split('\n');
              return (
                <Box component="ul" key={index} sx={{ pl: 2, mb: 2 }}>
                  {items.map((item, i) => (
                    <Typography component="li" key={i} variant="body1" paragraph>
                      {item.replace('- ', '')}
                    </Typography>
                  ))}
                </Box>
              );
            }
            // Regular paragraph
            return (
              <Typography key={index} variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                {paragraph}
              </Typography>
            );
          })}
        </ArticleContent>

        {/* CTA */}
        <Box sx={{ mt: 6, p: 4, bgcolor: 'primary.light', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            {t('blogCTATitle')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('blogCTAText')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
          >
            {t('getStartedFree')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default BlogPost;
